import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Typography,
  useTheme,
  Switch,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  LinearProgress,
  alpha,
  Grid,
  IconButton,
  Tooltip,
  ListItemText,
  Chip,
  Collapse,
  Button,
  FormControlLabel,
  Divider,
  useMediaQuery,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormGroup
} from '@mui/material';
import { getContrastText } from '../utils/colorUtils';
import MonthlyStats from './MonthlyStats';
import { soundService } from '../services/soundService';
import { useAuth } from '../contexts/AuthContext';
import { 
  formatDateForFirebase, 
  toLocalTime, 
  toUTC, 
  convertJSDayToSystemDay,
  isSameDay, 
  months 
} from '../utils/dateUtils';
import { format } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';
import { getQuoteForDay } from '../utils/dailyQuotes';
import WeeklyDonutChart from './WeeklyDonutChart';
import DailyProgressChart from './DailyProgressChart';
import { logError } from '../services/logService';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import TitleIcon from '@mui/icons-material/Title';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { doc, updateDoc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import SaveIcon from '@mui/icons-material/Save';
import SettingsIcon from '@mui/icons-material/Settings';
import { collection, query, where, getDocs, orderBy, arrayRemove, arrayUnion } from 'firebase/firestore';
import EventIcon from '@mui/icons-material/Event';
import EditIcon from '@mui/icons-material/Edit';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CloseIcon from '@mui/icons-material/Close';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import StarIcon from '@mui/icons-material/Star';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

const WEEKDAYS = [
  { id: 1, name: 'Segunda', short: 'Seg' },
  { id: 2, name: 'Terça', short: 'Ter' },
  { id: 3, name: 'Quarta', short: 'Qua' },
  { id: 4, name: 'Quinta', short: 'Qui' },
  { id: 5, name: 'Sexta', short: 'Sex' },
  { id: 6, name: 'Sábado', short: 'Sáb' },
  { id: 7, name: 'Domingo', short: 'Dom' }
];

const DEFAULT_PILLAR_COLORS = {
  vitalidade: '#2E7D32',
  mentalidade: '#F9A825',
  prosperidade: '#E65100'
};

const PILLARS = [
  { id: 'vitalidade', label: 'Vitalidade' },
  { id: 'mentalidade', label: 'Mentalidade' },
  { id: 'prosperidade', label: 'Prosperidade' }
];

const componentStyles = {
  width: '100%',
  position: 'relative',
  mb: { xs: 1, sm: 2 }
};

const DEFAULT_LAYOUT = {
  componentsOrder: ['calendar', 'progress', 'donuts', 'area', 'stats', 'events'],
  componentsVisibility: {
    calendar: true,
    progress: true,
    donuts: true,
    area: true,
    stats: true,
    events: true
  },
  showTitles: true
};

// Função auxiliar para verificar se um hábito está completo em uma data
function isHabitCompletedOnDate(habit, dateStr) {
  if (!habit?.completedDates) return false;
  
  return habit.completedDates.some(completedDate => 
    isSameDay(completedDate, dateStr)
  );
}

// Função auxiliar para obter a data de início do hábito em horário local (sem problemas de fuso)
function getHabitStartDate(habit) {
  if (!habit || !habit.startDate) return null;
  // Espera-se formato 'yyyy-MM-dd'
  const parts = habit.startDate.split('-');
  if (parts.length !== 3) return null;
  const [year, month, day] = parts.map(Number);
  if (!year || !month || !day) return null;
  const start = new Date(year, month - 1, day);
  start.setHours(0, 0, 0, 0);
  return start;
}

// Função auxiliar para verificar se um hábito está ativo em um dia da semana
function isHabitActiveOnDay(habit, dateStr) {
  if (!habit || !habit.active) return false;
  
  const date = toLocalTime(dateStr);
  if (!date) return false;

  // Se o hábito tem data de início, ignora dias anteriores a essa data
  const start = getHabitStartDate(habit);
  if (start && date < start) return false;
  
  const jsDay = date.getDay();
  const systemDay = convertJSDayToSystemDay(jsDay);
  
  // Se não tem dias específicos, está ativo todos os dias
  if (!habit.weekdays || habit.weekdays.length === 0) {
    return true;
  }
  
  return habit.weekdays.includes(systemDay);
}

// Função auxiliar para verificar se todos os hábitos estão completos em uma data
function isDayComplete(habits, dateStr) {
  if (!habits || !Array.isArray(habits)) return false;
  
  const activeHabits = habits.filter(habit => 
    habit && 
    habit.active !== false && 
    isHabitActiveOnDay(habit, dateStr)
  );
  
  if (activeHabits.length === 0) return false;
  
  return activeHabits.every(habit => 
    isHabitCompletedOnDate(habit, dateStr)
  );
}

function MonthCalendar({ 
  habits = [], 
  month, 
  year, 
  onToggleHabit, 
  onUpdateHabit, 
  onMonthChange,
  customColors,
  showDailyQuote = true,
  componentsOrder = ['calendar', 'progress', 'donuts', 'area', 'stats', 'events'],
  onUpdateCustomColors,
  onYearChange,
  onEditEvent,
  events = [],
  setEvents,
  onEditHabitFromCalendar, // opcional: abrir o mesmo modal da tela de Hábitos
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { currentUser } = useAuth();
  const [weeklyStats, setWeeklyStats] = useState([]);
  const [dailyProgress, setDailyProgress] = useState([]);
  const [filter, setFilter] = useState('all');
  const [localComponentsOrder, setLocalComponentsOrder] = useState([
    'calendar',
    'progress',
    'donuts',
    'area',
    'stats',
    'events'
  ]);
  const [visibleComponents, setVisibleComponents] = useState({
    calendar: true,
    progress: true,
    donuts: true,
    area: true,
    stats: true,
    events: true
  });
  const progressCache = useRef(new Map());
  const isDayCompleteCache = useRef(new Map());
  const [showTitles, setShowTitles] = useState(true);
  const [isControlsExpanded, setIsControlsExpanded] = useState(false);
  const lastUpdateTimestamp = useRef(0);
  const updateTimeout = useRef(null);
  const pendingUpdates = useRef({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [saveButtonState, setSaveButtonState] = useState({
    text: 'Salvar Layout',
    color: 'primary',
    disabled: false
  });
  const [showControls, setShowControls] = useState(false);
  const monthsContainerRef = useRef(null);
  const [showHabitIcons, setShowHabitIcons] = useState(customColors?.showHabitIcons !== false);
  const [legendOpen, setLegendOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [pillarFilter, setPillarFilter] = useState('all');
  const [calendarViewMode, setCalendarViewMode] = useState('normal'); // 'normal' | 'pillar'
  const [editActive, setEditActive] = useState(true);
  const [editingHabit, setEditingHabit] = useState(null);
  const [editName, setEditName] = useState('');
  const [editWeekdays, setEditWeekdays] = useState([]);
  const [editStartDate, setEditStartDate] = useState('');
  const [editPillar, setEditPillar] = useState('vitalidade');

  const getPillarColor = (pillarId) => {
    const id = (pillarId === 'expansao' ? 'mentalidade' : (pillarId || 'vitalidade'));
    return customColors?.pillars?.[id] || DEFAULT_PILLAR_COLORS[id] || DEFAULT_PILLAR_COLORS.vitalidade;
  };

  const handleViewModeChange = (event, newValue) => {
    if (!newValue) return;
    setCalendarViewMode(newValue);
  };

  const handleOpenEditHabit = (habit) => {
    if (!habit) return;
    // Se a tela pai fornecer um handler externo, usamos ele (ex: modal unificado de Hábitos)
    if (typeof onEditHabitFromCalendar === 'function') {
      onEditHabitFromCalendar(habit);
      return;
    }

    // Fallback: modal interno do calendário
    setEditingHabit(habit);
    setEditName(habit.name || '');
    setEditWeekdays(habit.weekdays || []);
    setEditStartDate(habit.startDate || '');
    setEditPillar(habit.pillar || 'vitalidade');
    setEditActive(habit.active !== false);
  };

  const handleCloseEditHabit = () => {
    setEditingHabit(null);
    setEditName('');
    setEditWeekdays([]);
    setEditStartDate('');
    setEditPillar('vitalidade');
    setEditActive(true);
  };

  const handleEditDayToggle = (dayId) => {
    setEditWeekdays(prev =>
      prev.includes(dayId)
        ? prev.filter(d => d !== dayId)
        : [...prev, dayId].sort()
    );
  };

  const handleSaveEditHabit = () => {
    const trimmedName = editName.trim();
    if (!editingHabit) return;

    if (trimmedName.length < 3) {
      alert('O nome do hábito deve ter pelo menos 3 caracteres.');
      return;
    }

    const isDuplicate = habits.some(h =>
      h.id !== editingHabit.id &&
      String(h.name || '').toLowerCase() === trimmedName.toLowerCase()
    );

    if (isDuplicate) {
      alert('Já existe um hábito com este nome.');
      return;
    }

    onUpdateHabit(editingHabit.id, {
      name: trimmedName,
      weekdays: editWeekdays,
      // Se não houver dias selecionados, desativa o hábito
      active: editWeekdays.length > 0 ? editActive : false,
      startDate: editStartDate || editingHabit.startDate || null,
      pillar: editPillar || editingHabit.pillar || 'vitalidade'
    });

    handleCloseEditHabit();
  };

  // Efeito para carregar as preferências do usuário
  useEffect(() => {
    const loadUserPreferences = async () => {
      if (currentUser?.uid) {
        try {
          const userRef = doc(db, 'users', currentUser.uid);
          const userDoc = await getDoc(userRef);
          
          if (userDoc.exists()) {
            const preferences = userDoc.data()?.preferences || {};
            
            // Carrega as configurações de visibilidade
            const savedVisibility = preferences.componentsVisibility || {};
            setVisibleComponents({
              calendar: true, // Força o calendário a estar sempre visível inicialmente
              progress: savedVisibility.progress ?? true,
              donuts: savedVisibility.donuts ?? true,
              area: savedVisibility.area ?? true,
              stats: savedVisibility.stats ?? true,
              events: savedVisibility.events ?? true
            });

            // Carrega a ordem dos componentes
            const savedOrder = preferences.calendarComponentsOrder || [];
            if (!savedOrder.includes('calendar')) {
              savedOrder.unshift('calendar'); // Garante que o calendário está na ordem
            }
            if (!savedOrder.includes('events')) {
              savedOrder.push('events'); // Garante que eventos está incluído
            }
            setLocalComponentsOrder(savedOrder.length ? savedOrder : [
              'calendar',
              'progress',
              'donuts',
              'area',
              'stats',
              'events'
            ]);

            setShowTitles(preferences.showTitles ?? true);
          }
        } catch (error) {
          console.error('Erro ao carregar preferências do usuário:', error);
        }
      }
    };
    
    loadUserPreferences();
  }, [currentUser]);

  // Efeito para rolar para o mês atual
  useEffect(() => {
    if (monthsContainerRef.current) {
      const container = monthsContainerRef.current;
      const buttons = container.getElementsByTagName('button');
      if (buttons.length > month) {
        const buttonWidth = buttons[0].offsetWidth;
        const scrollPosition = buttonWidth * month;
        container.scrollLeft = scrollPosition;
      }
    }
  }, [month]);

  // Calcula o número de dias no mês
  const daysInMonth = useMemo(() => {
    return new Date(year, month + 1, 0).getDate();
  }, [month, year]);

  // Calcula o primeiro dia do mês (0 = Domingo, 6 = Sábado)
  const firstDayOfMonth = useMemo(() => {
    return new Date(year, month, 1).getDay();
  }, [month, year]);

  // Calcula estatísticas básicas
  const { totalHabits, activeHabits, inactiveHabits } = useMemo(() => {
    const total = habits.length;
    const active = habits.filter(h => h.active !== false).length;
    return {
      totalHabits: total,
      activeHabits: active,
      inactiveHabits: total - active
    };
  }, [habits]);

  // Calcula os dias do mês
  const days = useMemo(() => {
    return Array.from({ length: daysInMonth }, (_, i) => i + 1);
  }, [daysInMonth]);

  // Filtra os hábitos baseado no filtro selecionado
  const filteredHabits = useMemo(() => {
    if (!habits.length) return [];
    
    return habits.filter(habit => {
      if (!habit || !habit.id) return false;
      
      // Filtro por pilar
      if (pillarFilter !== 'all') {
        const habitPillar = habit.pillar || 'vitalidade';
        if (habitPillar !== pillarFilter) return false;
      }

      switch (filter) {
        case 'active':
          return habit.active !== false;
        case 'inactive':
          return habit.active === false;
        default:
          return true;
      }
    });
  }, [habits, filter, pillarFilter]);

  // Calcula estatísticas semanais usando useMemo para evitar recálculos desnecessários
    const calculatedWeeklyStats = useMemo(() => {
    const weeks = [];
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    let currentWeekStart = 1;

      while (currentWeekStart <= daysInMonth) {
      const weekEnd = Math.min(currentWeekStart + 6, daysInMonth);
      let completed = 0;
      let total = 0;

      for (let day = currentWeekStart; day <= weekEnd; day++) {
        const date = new Date(year, month, day);
        const scheduledHabits = habits.filter(habit => {
          if (!habit || !habit.active) return false;
            // Respeita a data de início do hábito
            const start = getHabitStartDate(habit);
            if (start && date < start) return false;
          const dayOfWeek = date.getDay();
          const adjustedDay = dayOfWeek === 0 ? 7 : dayOfWeek;
          return !habit.weekdays?.length || habit.weekdays.includes(adjustedDay);
        });

        total += scheduledHabits.length;
        completed += scheduledHabits.filter(habit => {
          const formattedDate = formatDateForFirebase(date);
          return habit.completedDates?.some(completedDate => 
            completedDate.split('T')[0] === formattedDate.split('T')[0]
          );
        }).length;
      }

      weeks.push({
        label: `Semana ${Math.ceil(currentWeekStart / 7)}`,
        completed,
        total
      });

      currentWeekStart = weekEnd + 1;
    }

    return weeks;
  }, [year, month, habits]);

  // Atualiza o estado apenas quando os stats calculados mudarem
  useEffect(() => {
    setWeeklyStats(calculatedWeeklyStats);
  }, [calculatedWeeklyStats]);

  // Calcula progresso diário usando useMemo para evitar recálculos desnecessários
    const calculatedDailyProgress = useMemo(() => {
    const dailyData = [];
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const scheduledHabits = habits.filter(habit => {
        if (!habit || !habit.active) return false;
          // Respeita a data de início do hábito
          const start = getHabitStartDate(habit);
          if (start && date < start) return false;
        const dayOfWeek = date.getDay();
        const adjustedDay = dayOfWeek === 0 ? 7 : dayOfWeek;
        return !habit.weekdays?.length || habit.weekdays.includes(adjustedDay);
      });

      if (scheduledHabits.length > 0) {
        const formattedDate = formatDateForFirebase(date);
      const completedHabits = scheduledHabits.filter(habit => 
          habit.completedDates?.some(completedDate => 
            completedDate.split('T')[0] === formattedDate.split('T')[0]
          )
      ).length;

        const completionRate = Math.round((completedHabits / scheduledHabits.length) * 100);
        dailyData.push({
          day,
          completion: completionRate
        });
      }
    }

    return dailyData;
  }, [year, month, habits]);

  // Atualiza o estado apenas quando o progresso calculado mudar
  useEffect(() => {
    setDailyProgress(calculatedDailyProgress);
  }, [calculatedDailyProgress]);

  // Limpa o cache quando os hábitos mudam para recalcular dias perfeitos em tempo real
  useEffect(() => {
    progressCache.current.clear();
    isDayCompleteCache.current.clear();
  }, [habits]);

  // Handler para alternar hábito
  const handleHabitToggle = useCallback(async (habit, dateStr) => {
    if (!habit || !habit.id) return;
    
    try {
      // Limpa os caches
      progressCache.current.clear();
      isDayCompleteCache.current.clear();

      const wasCompleted = isHabitCompletedOnDate(habit, dateStr);
      
      // Simula o estado atualizado do hábito antes da atualização do banco
      const updatedHabit = {
        ...habit,
        completedDates: wasCompleted 
          ? habit.completedDates.filter(date => !isSameDay(date, dateStr))
          : [...(habit.completedDates || []), dateStr]
      };

      // Atualiza os estados localmente antes da atualização do banco
      const updatedHabits = habits.map(h => h.id === habit.id ? updatedHabit : h);

      // Verifica se este toggle vai completar o dia
      const activeHabits = updatedHabits.filter(h => isHabitActiveOnDay(h, dateStr));
      const willCompletePerfectDay = !wasCompleted && activeHabits.every(h => {
        if (h.id === habit.id) return true;
        return isHabitCompletedOnDate(h, dateStr);
      });

      // Atualiza o hábito no banco de dados
      await onToggleHabit(habit.id, dateStr, !wasCompleted);

      // Tocar som de conclusão do hábito apenas se estiver ativado e estamos marcando como completo
      if (!wasCompleted) {
        soundService.playHabitComplete();
      }

      // Se completou o dia perfeito, toca o som
      if (willCompletePerfectDay) {
        soundService.playPerfectDay();
      }

    } catch (error) {
      logError('Erro ao alternar hábito', { error });
    }
  }, [habits, onToggleHabit]);

  // Handler para alternar estado ativo do hábito
  const handleActiveToggle = useCallback((habit) => {
    if (!habit || !habit.id) return;
    
    // Limpa os caches para recalcular dias perfeitos em tempo real
    progressCache.current.clear();
    isDayCompleteCache.current.clear();
    
    onUpdateHabit(habit.id, { 
      active: !habit.active 
    });
  }, [onUpdateHabit]);

  // Handler para mudança de filtro
  const handleFilterChange = useCallback((event, newValue) => {
    if (newValue !== null) {
      setFilter(newValue);
    }
  }, []);

  // Calcula o progresso geral
  const progress = useMemo(() => {
    if (!habits.length) return {
      percentage: 0,
      completed: 0,
      totalPossible: 0
    };
    
    let totalPossible = 0;
    let completed = 0;

    const activeHabitsArray = habits.filter(habit => habit.active !== false);
    
    activeHabitsArray.forEach(habit => {
      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(year, month, day);
        const dateStr = formatDateForFirebase(date);
        
        if (isHabitActiveOnDay(habit, dateStr)) {
          totalPossible++;
          if (isHabitCompletedOnDate(habit, dateStr)) {
            completed++;
          }
        }
      }
    });

    return {
      percentage: totalPossible === 0 ? 0 : (completed / totalPossible) * 100,
      completed,
      totalPossible
    };
  }, [habits, daysInMonth, month, year]);

  // Verifica se um dia está completo (todos os hábitos ativos foram concluídos)
  const checkDayComplete = useCallback((habits, dateStr) => {
    const cacheKey = `${dateStr}-${habits.map(h => h.id).join('-')}`;
    if (isDayCompleteCache.current.has(cacheKey)) {
      return isDayCompleteCache.current.get(cacheKey);
    }

    const isComplete = isDayComplete(habits, dateStr);
    isDayCompleteCache.current.set(cacheKey, isComplete);
    return isComplete;
  }, []);

  // Renderiza o gráfico de progresso diário
  const renderDailyProgressChart = useCallback(() => {
    if (!dailyProgress || dailyProgress.length === 0) {
      return null;
    }

    const sortedData = [...dailyProgress].sort((a, b) => a.day - b.day);
    
    return (
        <DailyProgressChart 
          data={sortedData}
          customColors={customColors}
        />
    );
  }, [dailyProgress, customColors]);

  const handleMoveComponent = useCallback((componentId, direction) => {
    console.log('Moving component:', componentId, direction);
    setLocalComponentsOrder(prevOrder => {
      const currentIndex = prevOrder.indexOf(componentId);
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
      if (newIndex >= 0 && newIndex < prevOrder.length) {
        const newOrder = [...prevOrder];
      const temp = newOrder[currentIndex];
      newOrder[currentIndex] = newOrder[newIndex];
      newOrder[newIndex] = temp;
        console.log('New order:', newOrder);
        setHasUnsavedChanges(true);
        return newOrder;
      }
      return prevOrder;
    });
  }, []);

  const handleToggleVisibility = useCallback((componentId) => {
    setVisibleComponents(prev => {
      const newVisibility = {
        ...prev,
        [componentId]: !prev[componentId]
      };
      setHasUnsavedChanges(true);
      return newVisibility;
    });
  }, []);

  const handleToggleTitles = useCallback(() => {
    console.log('Toggling titles');
    setShowTitles(prev => {
      const newValue = !prev;
      console.log('New showTitles value:', newValue);
      setHasUnsavedChanges(true);
      return newValue;
    });
  }, []);

  const handleSaveLayout = async () => {
    try {
      setSaveButtonState({
        text: 'Salvo!',
        color: 'success',
        disabled: true
      });

        const userRef = doc(db, 'users', currentUser.uid);
        await updateDoc(userRef, {
        'preferences.calendarComponentsOrder': localComponentsOrder,
        'preferences.componentsVisibility': visibleComponents,
        'preferences.showTitles': showTitles,
        updatedAt: new Date().toISOString()
      });

      // Aguarda 5 segundos antes de reverter o botão e escondê-lo
      setTimeout(() => {
        setSaveButtonState({
          text: 'Salvar Layout',
          color: 'primary',
          disabled: false
        });
        setHasUnsavedChanges(false);
      }, 3500);

    } catch (error) {
      console.error('Erro ao salvar layout:', error);
      setSaveButtonState({
        text: 'Erro',
        color: 'error',
        disabled: false
      });
    }
  };

  const handleRestoreDefaultLayout = () => {
    setLocalComponentsOrder(DEFAULT_LAYOUT.componentsOrder);
    setVisibleComponents(DEFAULT_LAYOUT.componentsVisibility);
    setShowTitles(DEFAULT_LAYOUT.showTitles);
    setHasUnsavedChanges(true);
  };

  const renderComponent = (componentId) => {
    // Verifica se o componente deve ser exibido
    if (!visibleComponents[componentId]) {
      return null;
    }

    const componentStyles = {
      width: '100%',
      position: 'relative',
      mb: { xs: 1, sm: 2 }
    };

    const renderTitle = (title) => {
      if (!showTitles) return null;
      return (
        <Typography 
          variant="h6" 
          color={customColors?.primary || 'primary.main'} 
          gutterBottom 
          sx={{ 
            pl: { xs: 1, sm: 0 },
            mb: { xs: 0.5, sm: 1 },
            fontSize: { xs: '1rem', sm: '1.25rem' }
          }}
        >
          {title}
        </Typography>
      );
    };

    switch (componentId) {
      case 'calendar':
        return (
          <Box key={componentId} sx={componentStyles}>
            {renderTitle('Hábitos')}
            {/* Filtro dinâmico por Pilar */}
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                mb: 1.5,
                pl: { xs: 1, sm: 0 }
              }}
            >
              <Button
                size="small"
                variant={pillarFilter === 'all' ? 'contained' : 'outlined'}
                onClick={() => setPillarFilter('all')}
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                  borderRadius: 999,
                  bgcolor: pillarFilter === 'all'
                    ? (customColors?.primary || theme.palette.primary.main)
                    : 'transparent',
                  color: pillarFilter === 'all'
                    ? (theme.palette.mode === 'dark' ? theme.palette.primary.contrastText : (customColors?.primary ? getContrastText(customColors.primary) : theme.palette.primary.contrastText))
                    : (theme.palette.text.primary),
                  '&:hover': {
                    bgcolor: pillarFilter === 'all'
                      ? (customColors?.primary || theme.palette.primary.main)
                      : alpha(theme.palette.text.primary, 0.06)
                  }
                }}
              >
                Todos
              </Button>
              {PILLARS.map(pillar => {
                const color = getPillarColor(pillar.id);
                const selected = pillarFilter === pillar.id;
                return (
                  <Button
                    key={pillar.id}
                    size="small"
                    variant={selected ? 'contained' : 'outlined'}
                    onClick={() => setPillarFilter(pillar.id)}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 500,
                      borderRadius: 999,
                      borderColor: color,
                      bgcolor: selected ? color : 'transparent',
                      color: selected ? getContrastText(color) : color,
                      '&:hover': {
                        bgcolor: selected
                          ? color
                          : alpha(color, 0.08)
                      }
                    }}
                  >
                    {pillar.label}
                  </Button>
                );
              })}
            </Box>
            {/* Modo de visão: Normal x Por Pilar */}
            <Box sx={{ mb: 1.5, pl: { xs: 1, sm: 0 } }}>
              <ToggleButtonGroup
                value={calendarViewMode}
                exclusive
                onChange={handleViewModeChange}
                size="small"
              >
                <ToggleButton value="normal">
                  Modo Normal
                </ToggleButton>
                <ToggleButton value="pillar">
                  Visão por Pilar
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <TableContainer 
              component={Paper} 
              sx={{ 
                mb: { xs: 1, sm: 2 },
                overflow: 'auto',
                position: 'relative',
                borderRadius: { xs: 0, sm: 1 },
                '& .MuiTableCell-root': {
                  whiteSpace: 'nowrap',
                  borderRight: `1px solid ${theme.palette.divider}`,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  padding: '8px 4px',
                  verticalAlign: 'middle'
                },
                '& .sticky-column': {
                  position: 'sticky',
                  left: 0,
                  background: theme.palette.background.paper,
                  zIndex: 2,
                  verticalAlign: 'middle'
                },
                '& .sticky-header': {
                  position: 'sticky',
                  left: 0,
                  background: customColors?.primary || theme.palette.primary.main,
                  zIndex: 3,
                  verticalAlign: 'middle'
                },
                '& .MuiTableHead-root': {
                  backgroundColor: customColors?.primary || theme.palette.primary.main,
                  '& .MuiTableCell-root': {
                    color: theme.palette.mode === 'dark' ? theme.palette.primary.contrastText : (customColors?.primary ? getContrastText(customColors.primary) : theme.palette.primary.contrastText),
                    borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
                    borderRight: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
                    padding: '8px 4px',
                    verticalAlign: 'middle'
                  }
                },
                '& .MuiTable-root': {
                  borderCollapse: 'separate',
                  borderSpacing: 0,
                  '& .MuiTableRow-root': {
                    '& .MuiTableCell-root': {
                      verticalAlign: 'middle'
                    }
                  }
                }
              }}
            >
              <Table 
                size="small" 
                sx={{
                  borderCollapse: 'separate',
                  borderSpacing: 0,
                  '& .MuiTableHead-root': {
                    backgroundColor: customColors?.primary || theme.palette.primary.main,
                    '& .MuiTableCell-root': {
                      padding: '8px 4px',
                      verticalAlign: 'middle',
                      lineHeight: 1.2
                    }
                  }
                }}
              >
                <TableHead>
                  <TableRow sx={{ 
                    backgroundColor: customColors?.primary || theme.palette.primary.main,
                    '& th': {
                      color: theme.palette.mode === 'dark' ? theme.palette.primary.contrastText : (customColors?.primary ? getContrastText(customColors.primary) : theme.palette.primary.contrastText),
                      padding: '8px 4px',
                      height: '100%'
                    }
                  }}>
                    <TableCell 
                      align="center"
                      className="sticky-header"
                      sx={{ 
                        width: '5px',
                        fontWeight: 'bold',
                        textAlign: 'center',
                        padding: '8px 4px',
                        borderRight: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
                        borderLeft: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
                        height: '100%'
                      }}
                    >
                      Ativo
                    </TableCell>
                    <TableCell 
                      className="sticky-header"
                      sx={{ 
                        width: '200px',
                        fontWeight: 'bold',
                        padding: '8px',
                        borderRight: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
                        borderLeft: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
                        height: '100%'
                      }}
                    >
                      Hábitos
                    </TableCell>
                    {days.map(day => (
                      <TableCell 
                        key={day} 
                        align="center" 
                        sx={{ 
                          width: '28px',
                          minWidth: '28px',
                          maxWidth: '28px',
                          padding: '4px 0',
                          color: theme.palette.mode === 'dark' ? theme.palette.primary.contrastText : (customColors?.primary ? getContrastText(customColors.primary) : theme.palette.primary.contrastText),
                          borderRight: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
                          display: 'table-cell',
                          verticalAlign: 'middle',
                          '& > div': {
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%'
                          }
                        }}
                      >
                        <div>
                          <Typography variant="caption" display="block" sx={{ 
                            fontSize: '0.7rem',
                            lineHeight: 1.2
                          }}>
                            {String(day)}
                          </Typography>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              opacity: 0.8,
                              fontSize: '0.65rem',
                              lineHeight: 1.2,
                              display: 'block',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {new Date(year, month, day).toLocaleDateString('pt-BR', { weekday: 'short' }).slice(0, 3)}
                          </Typography>
                        </div>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(calendarViewMode === 'normal'
                    ? filteredHabits.map(habit => ({ habit, isPillarHeader: false }))
                    : PILLARS.flatMap(pillar => {
                        const pillarHabits = filteredHabits.filter(h => (h.pillar || 'vitalidade') === pillar.id);
                        if (!pillarHabits.length) return [];
                        const color = getPillarColor(pillar.id);
                        return [
                          { isPillarHeader: true, pillar, color },
                          ...pillarHabits.map(habit => ({ habit, isPillarHeader: false }))
                        ];
                      })
                  ).map((row, index) => {
                    if (row.isPillarHeader) {
                      return (
                        <TableRow
                          key={`pillar-${row.pillar.id}`}
                          sx={{
                            backgroundColor: alpha(row.color, 0.06)
                          }}
                        >
                          <TableCell
                            colSpan={2 + days.length}
                            sx={{
                              fontWeight: 600,
                              color: row.color,
                              borderLeft: `4px solid ${row.color}`
                            }}
                          >
                            {row.pillar.label}
                          </TableCell>
                        </TableRow>
                      );
                    }

                    const habit = row.habit;
                    if (!habit || !habit.id) return null;
                    
                    const safeHabit = {
                      id: habit.id,
                      name: habit.name || 'Sem nome',
                      active: habit.active !== false,
                      weekdays: habit.weekdays || [],
                      completedDates: habit.completedDates || [],
                      startDate: habit.startDate ?? null,
                      pillar: habit.pillar || 'vitalidade'
                    };
                    
                    return (
                      <TableRow 
                        key={safeHabit.id}
                        sx={{ 
                          '&:nth-of-type(odd)': {
                            backgroundColor: theme.palette.action.hover
                          }
                        }}
                      >
                        <TableCell 
                          align="center" 
                          className="sticky-column"
                          sx={{ 
                            padding: '1px',
                            textAlign: 'center',
                            bgcolor: theme.palette.background.paper,
                            borderRight: `1px solid ${theme.palette.divider}`,
                            borderLeft: `1px solid ${theme.palette.divider}`
                          }}
                        >
                          <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%'
                          }}>
                            <Switch
                              checked={safeHabit.active}
                              onChange={() => handleActiveToggle(safeHabit)}
                              size="small"
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                  color: customColors?.primary || theme.palette.primary.main,
                                  '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                  }
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                  backgroundColor: customColors?.primary || theme.palette.primary.main
                                }
                              }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell 
                          component="th" 
                          scope="row"
                          className="sticky-column"
                          sx={{ 
                            fontWeight: 'medium',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            padding: '8px',
                            bgcolor: theme.palette.background.paper,
                            borderRight: `1px solid ${theme.palette.divider}`,
                            borderLeft: `4px solid ${getPillarColor(safeHabit.pillar)}`
                          }}
                        >
                          <Box
                            sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}
                          >
                            <Tooltip
                              title={
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                    Estatísticas do Mês
                                  </Typography>
                                  <Typography variant="body2">
                                    Programado: {habitStats.stats[safeHabit.id]?.scheduled || 0} {habitStats.stats[safeHabit.id]?.scheduled === 1 ? 'vez' : 'vezes'}
                                  </Typography>
                                  <Typography variant="body2">
                                    Realizado: {habitStats.stats[safeHabit.id]?.completed || 0} {habitStats.stats[safeHabit.id]?.completed === 1 ? 'vez' : 'vezes'}
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 0.5 }}>
                                    Eficiência: {
                                      habitStats.stats[safeHabit.id]?.scheduled > 0
                                        ? `${Math.round((habitStats.stats[safeHabit.id].completed / habitStats.stats[safeHabit.id].scheduled) * 100)}%`
                                        : '0%'
                                    }
                                  </Typography>
                                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                                    Início: {safeHabit.startDate
                                      ? (() => {
                                          const [y, m, d] = safeHabit.startDate.split('-');
                                          return d && m && y ? `${d}/${m}/${y}` : 'A definir';
                                        })()
                                      : 'A definir'}
                                  </Typography>
                                </Box>
                              }
                              arrow
                              placement="right"
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  cursor: 'pointer'
                                }}
                                onClick={() => handleOpenEditHabit(safeHabit)}
                              >
                                <Typography sx={{ fontWeight: 'bold', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                  {String(safeHabit.name)}
                                </Typography>
                                {showHabitIcons && (
                                  <>
                                    {habitStats.stats[safeHabit.id]?.fireStreak >= 3 && <WhatshotIcon fontSize="small" color="warning" titleAccess="Sequência de dias (streak)" />}
                                    {habitStats.maxStreakHabitId === safeHabit.id && <EmojiEventsIcon fontSize="small" color="success" titleAccess="Maior streak do mês" />}
                                    {habitStats.stats[safeHabit.id]?.allCompleted && <StarIcon fontSize="small" color="primary" titleAccess="Realizou todos os dias programados" />}
                                  </>
                                )}
                              </Box>
                            </Tooltip>
                            {customColors?.showProgrammedDays !== false && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ScheduleIcon sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }} />
                                <Typography variant="body2" component="span" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                  {(() => {
                                    if (!safeHabit.weekdays || safeHabit.weekdays.length === 0) return 'Todos os Dias';
                                    const allDays = [1, 2, 3, 4, 5, 6, 7];
                                    const hasAllDays = allDays.every(day => safeHabit.weekdays.includes(day));
                                    if (hasAllDays) return 'Todos os Dias';
                                    return WEEKDAYS
                                      .filter(day => safeHabit.weekdays.includes(day.id))
                                      .map(day => day.short)
                                      .join(', ');
                                  })()}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                        {days.map(day => {
                          const date = new Date(year, month, day);
                          const dateStr = date.toISOString().split('T')[0];
                          const isActive = isHabitActiveOnDay(safeHabit, dateStr);
                          const formattedDateToCheck = formatDateForFirebase(date);
                          const isCompleted = isActive && safeHabit.completedDates?.some(completedDate => {
                            const completedDateOnly = completedDate.split('T')[0];
                            const formattedDateOnly = formattedDateToCheck.split('T')[0];
                            return completedDateOnly === formattedDateOnly;
                          });
                          const allCompleted = checkDayComplete(habits, dateStr);
                          
                          const today = new Date();
                          const isToday = day === today.getDate() && 
                                        month === today.getMonth() && 
                                        year === today.getFullYear();
                          
                          return (
                            <TableCell 
                              key={`${safeHabit.id}-${day}`}
                              align="center" 
                              padding="none"
                              sx={{
                                width: '28px',
                                minWidth: '28px',
                                maxWidth: '28px',
                                backgroundColor: allCompleted && customColors?.highlightPerfectDay !== false ? 
                                  alpha(theme.palette.success.main, 0.3) : 
                                  (isToday && customColors?.highlightToday !== false) ? 
                                    '#FFF9C4' : theme.palette.background.paper,
                                position: 'relative',
                                '&::before': {
                                  content: '""',
                                  position: 'absolute',
                                  top: 0,
                                  right: 0,
                                  bottom: 0,
                                  left: 0,
                                  opacity: isActive ? 1 : 0.3,
                                  backgroundColor: 'inherit',
                                  pointerEvents: 'none'
                                },
                                borderRight: `1px solid ${theme.palette.divider}`,
                                borderBottom: `1px solid ${theme.palette.divider}`,
                                '& .MuiCheckbox-root': {
                                  opacity: isActive ? 1 : 0.25,
                                  position: 'relative',
                                  zIndex: 1,
                                  '& .MuiSvgIcon-root': {
                                    fontSize: '1rem',
                                    color: !isActive ? theme.palette.grey[500] :
                                      (allCompleted ? theme.palette.success.main : 
                                        (customColors?.primary || theme.palette.primary.main))
                                  }
                                },
                                '&:hover': {
                                  backgroundColor: allCompleted && customColors?.highlightPerfectDay !== false ? 
                                    alpha(theme.palette.success.main, 0.4) : 
                                    (isToday && customColors?.highlightToday !== false) ? 
                                      '#FFF176' : alpha(theme.palette.action.hover, 0.1)
                                }
                              }}
                            >
                              <Checkbox
                                checked={isCompleted}
                                onChange={() => handleHabitToggle(safeHabit, dateStr)}
                                disabled={!isActive}
                                sx={{ 
                                  p: 0.25,
                                  '& .MuiSvgIcon-root': {
                                    fontSize: '0.9rem',
                                    color: !isActive ? theme.palette.grey[500] :
                                      (allCompleted ? theme.palette.success.main : 
                                        (customColors?.primary || theme.palette.primary.main))
                                  },
                                  color: customColors?.primary || theme.palette.primary.main,
                                  '&.Mui-checked': {
                                    color: allCompleted ? theme.palette.success.main : (customColors?.primary || theme.palette.primary.main)
                                  }
                                }}
                              />
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {/* Legenda das cores do calendário */}
            {isMobile ? (
              <>
                <Button variant="outlined" size="small" sx={{ mt: 1, mb: 2, ml: 1 }} onClick={() => setLegendOpen(true)}>
                  Ver legenda
                </Button>
                <Dialog open={legendOpen} onClose={() => setLegendOpen(false)} fullWidth maxWidth="xs">
                  <DialogTitle>Legenda do Calendário</DialogTitle>
                  <DialogContent dividers>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 20, height: 20, borderRadius: 1, bgcolor: (customColors?.highlightPerfectDay !== false ? alpha(theme.palette.success.main, 0.3) : theme.palette.background.paper), border: `1.5px solid ${theme.palette.success.main}`, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 0 }}>
                          <Checkbox checked disabled sx={{ p: 0, '& .MuiSvgIcon-root': { fontSize: 16, color: theme.palette.success.main } }} />
                        </Box>
                        <Typography variant="caption">Dia perfeito</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Checkbox checked disabled sx={{ p: 0, '& .MuiSvgIcon-root': { fontSize: 20, color: customColors?.primary || theme.palette.primary.main } }} />
                        <Typography variant="caption">Hábito realizado</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Checkbox checked={false} disabled sx={{ p: 0, '& .MuiSvgIcon-root': { fontSize: 20, color: customColors?.primary || theme.palette.primary.main, opacity: 0.5 } }} />
                        <Typography variant="caption">Dia com hábito programado</Typography>
                      </Box>
                      {customColors?.highlightToday !== false && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 20, height: 20, borderRadius: 1, bgcolor: '#FFF9C4', border: `1.5px solid ${theme.palette.warning.main}` }} />
                          <Typography variant="caption">Dia atual</Typography>
                        </Box>
                      )}
                      {showHabitIcons && (
                        <>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <WhatshotIcon fontSize="small" color="warning" />
                            <Typography variant="caption">Streak (sequência de dias)</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <EmojiEventsIcon fontSize="small" color="success" />
                            <Typography variant="caption">Maior streak do mês</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <StarIcon fontSize="small" color="primary" />
                            <Typography variant="caption">Realizou todos os dias programados</Typography>
                          </Box>
                        </>
                      )}
                    </Box>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setLegendOpen(false)} color="primary" variant="contained">Fechar</Button>
                  </DialogActions>
                </Dialog>
              </>
            ) : (
              <Box sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                mt: 1,
                mb: 2
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 20, height: 20, borderRadius: 1, bgcolor: (customColors?.highlightPerfectDay !== false ? alpha(theme.palette.success.main, 0.3) : theme.palette.background.paper), border: `1.5px solid ${theme.palette.success.main}`, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 0 }}>
                    <Checkbox checked disabled sx={{ p: 0, '& .MuiSvgIcon-root': { fontSize: 16, color: theme.palette.success.main } }} />
                  </Box>
                  <Typography variant="caption">Dia perfeito</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Checkbox checked disabled sx={{ p: 0, '& .MuiSvgIcon-root': { fontSize: 20, color: customColors?.primary || theme.palette.primary.main } }} />
                  <Typography variant="caption">Hábito realizado</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Checkbox checked={false} disabled sx={{ p: 0, '& .MuiSvgIcon-root': { fontSize: 20, color: customColors?.primary || theme.palette.primary.main, opacity: 0.5 } }} />
                  <Typography variant="caption">Dia com hábito programado</Typography>
                </Box>
                {customColors?.highlightToday !== false && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 20, height: 20, borderRadius: 1, bgcolor: '#FFF9C4', border: `1.5px solid ${theme.palette.warning.main}` }} />
                    <Typography variant="caption">Dia atual</Typography>
                  </Box>
                )}
                {showHabitIcons && (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <WhatshotIcon fontSize="small" color="warning" />
                      <Typography variant="caption">Streak (sequência de dias)</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EmojiEventsIcon fontSize="small" color="success" />
                      <Typography variant="caption">Maior streak do mês</Typography>
                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <StarIcon fontSize="small" color="primary" />
                  <Typography variant="caption">Realizou todos os dias programados</Typography>
                </Box>
                  </>
                )}
              </Box>
            )}
          </Box>
        );
      case 'progress':
        return (
          <Box key={componentId} sx={componentStyles}>
            {renderTitle('Barra de Progresso')}
            <Box sx={{ width: '100%' }}>
              <Paper sx={{ 
                p: { xs: 1, sm: 1.5 },
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                width: '100%',
                borderRadius: { xs: 0, sm: 1 }
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: { xs: 0.5, sm: 1 }, 
                  mb: { xs: 0.5, sm: 2 }
                }}>
                  <Typography variant="h6" sx={{ 
                    flexGrow: 1,
                    fontSize: { xs: '0.875rem', sm: '1.25rem' }
                  }}>
                    Progresso de {months[month]}/{String(year).slice(-2)}
                  </Typography>
                  <Typography 
                    variant="h4" 
                    sx={{ 
                      ml: { xs: 0.5, sm: 2 },
                      fontWeight: 'bold',
                      color: customColors?.primary || theme.palette.primary.main,
                      fontSize: { xs: '1.25rem', sm: '2.125rem' }
                    }}
                  >
                    {Math.round(progress?.percentage || 0)}%
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={progress?.percentage || 0} 
                  sx={{ 
                    height: { xs: 6, sm: 10 },
                    borderRadius: 5,
                    backgroundColor: theme.palette.grey[200],
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: customColors?.primary || theme.palette.primary.main,
                      borderRadius: 5
                    }
                  }}
                />
                <Typography
                  variant="body2" 
                  sx={{ 
                    mt: { xs: 0.5, sm: 1 },
                    textAlign: 'right',
                    color: theme.palette.text.secondary,
                    fontSize: { xs: '0.7rem', sm: '0.875rem' }
                  }}
                >
                  {progress?.completed || 0} de {progress?.totalPossible || 0} hábitos programados
                </Typography>
              </Paper>
            </Box>
          </Box>
        );
      case 'donuts':
        return (
          <Box key={componentId} sx={componentStyles}>
            {renderTitle('Gráficos Donuts')}
            <Grid container spacing={{ xs: 1, sm: 1.5 }}>
              {weeklyStats.map((week, index) => (
                <Grid item xs={6} sm={weeklyStats.length === 4 ? 3 : 2.4} key={index}>
                  <WeeklyDonutChart
                    weekLabel={`Semana ${index + 1}`}
                    completed={week.completed}
                    total={week.total}
                    customColors={customColors}
                    totalWeeks={weeklyStats.length}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        );
      case 'area':
        return (
          <Box key={componentId} sx={componentStyles}>
            {renderTitle('Gráfico de Área')}
            <Paper sx={{ 
              p: { xs: 1, sm: 1.5 },
              borderRadius: { xs: 0, sm: 1 }
            }}>
              {renderDailyProgressChart()}
            </Paper>
          </Box>
        );
      case 'stats':
        return (
          <Box key={componentId} sx={componentStyles}>
            {renderTitle('Cards de Estatísticas')}
            <MonthlyStats
              habits={habits}
              month={month}
              year={year}
              customColors={customColors}
            />
          </Box>
        );
      case 'events':
        return (
          <Box key={componentId} sx={componentStyles}>
            {renderTitle('Eventos do Mês')}
            <Paper sx={{ p: { xs: 1, sm: 1.5 }, borderRadius: { xs: 0, sm: 1 } }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: { xs: 60, sm: 'auto' }, fontSize: { xs: '0.85rem', sm: '1rem' }, p: { xs: '4px 2px', sm: '8px 4px' } }}>Data</TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.85rem', sm: '1rem' }, p: { xs: '4px 2px', sm: '8px 4px' } }}>Evento</TableCell>
                      {!isMobile && (
                        <TableCell sx={{ width: { xs: 70, sm: 'auto' }, fontSize: { xs: '0.85rem', sm: '1rem' }, p: { xs: '4px 2px', sm: '8px 4px' } }}>Horário</TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(() => {
                      const filteredEvents = events
                        .filter(event => 
                          event.date.getMonth() === month &&
                          event.date.getFullYear() === year
                        )
                        .sort((a, b) => {
                          const dateDiff = a.date - b.date;
                          if (dateDiff !== 0) return dateDiff;
                          if (a.time && b.time) {
                            return a.time - b.time;
                          } else if (a.time && !b.time) {
                            return -1;
                          } else if (!a.time && b.time) {
                            return 1;
                          } else {
                            return 0;
                          }
                        });
                      if (filteredEvents.length === 0) {
                        return (
                          <TableRow>
                            <TableCell colSpan={isMobile ? 2 : 3} align="center">
                              Você não possui eventos para este mês
                            </TableCell>
                          </TableRow>
                        );
                      }
                      return filteredEvents.map((event) => (
                        <TableRow key={event.id} sx={{
                          '& > td': {
                            p: { xs: '4px 2px', sm: '8px 4px' },
                            fontSize: { xs: '0.85rem', sm: '1rem' },
                            whiteSpace: 'nowrap',
                            maxWidth: { xs: 90, sm: 'none' },
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }
                        }}>
                          <TableCell sx={{ verticalAlign: isMobile ? 'top' : 'middle', minWidth: 60 }}>
                            {event.date instanceof Date && !isNaN(event.date) 
                              ? (isMobile
                                ? <Box>
                                    <div>{format(event.date, 'dd/MM')}</div>
                                    {event.time instanceof Date && !isNaN(event.time) && (
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                        {format(event.time, 'HH:mm')}
                                      </Typography>
                                    )}
                                  </Box>
                                : format(event.date, "dd 'de' MMMM", { locale: ptBR })
                              )
                              : 'Data inválida'}
                          </TableCell>
                          <TableCell
                            sx={{
                              width: isMobile ? '100%' : undefined,
                              p: { xs: '4px 2px', sm: '8px 4px' },
                              fontSize: { xs: '0.85rem', sm: '1rem' }
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: isMobile ? '100%' : 'auto' }}>
                              <EventIcon color="primary" sx={{ fontSize: { xs: 18, sm: 24 } }} />
                              <Typography component="span" sx={
                                isMobile
                                  ? {
                                      fontSize: '0.95rem',
                                      fontWeight: 500,
                                      width: '100%',
                                      whiteSpace: 'normal',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                      maxWidth: 'none',
                                    }
                                  : {
                                      fontSize: '1rem',
                                      fontWeight: 500,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      maxWidth: 400,
                                    }
                              }>
                                {event.title}
                              </Typography>
                              {typeof onEditEvent === 'function' && (
                                <IconButton size="small" onClick={() => onEditEvent(event)} sx={{ ml: 0.5 }}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                            {event.description && (
                              <Typography variant="body2" color="text.secondary" sx={
                                isMobile
                                  ? {
                                      fontSize: '0.8rem',
                                      width: '100%',
                                      whiteSpace: 'normal',
                                      overflow: 'visible',
                                      textOverflow: 'unset',
                                      maxWidth: 'none',
                                      display: 'block',
                                    }
                                  : {
                                      fontSize: '0.9rem',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      maxWidth: 200,
                                    }
                              }>
                                {event.description}
                              </Typography>
                            )}
                          </TableCell>
                          {!isMobile && (
                            <TableCell>
                              {event.time instanceof Date && !isNaN(event.time)
                                ? format(event.time, 'HH:mm')
                                : 'Não especificado'}
                            </TableCell>
                          )}
                        </TableRow>
                      ));
                    })()}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Box>
        );
      default:
        return null;
    }
  };

  const renderControls = () => {
    return (
      <Dialog open={showControls} onClose={() => setShowControls(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 2 }}>
          Configurações do Calendário
          <IconButton onClick={() => setShowControls(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3 }}>
            {/* Lado Esquerdo: Visualização do Calendário + Filtros */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Visualização do Calendário
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={showTitles}
                    onChange={handleToggleTitles}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: customColors?.primary || 'primary.main'
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: customColors?.primary || 'primary.main'
                      }
                    }}
                  />
                }
                label={<Typography variant="body2">Mostrar títulos das seções</Typography>}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={customColors?.showDailyQuote !== false}
                    onChange={() => onUpdateCustomColors({
                      ...customColors,
                      showDailyQuote: customColors?.showDailyQuote === false
                    })}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: customColors?.primary || 'primary.main'
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: customColors?.primary || 'primary.main'
                      }
                    }}
                  />
                }
                label={<Typography variant="body2">Mostrar frase do dia</Typography>}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={customColors?.highlightToday !== false}
                    onChange={() => onUpdateCustomColors({
                      ...customColors,
                      highlightToday: customColors?.highlightToday === false
                    })}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: customColors?.primary || 'primary.main'
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: customColors?.primary || 'primary.main'
                      }
                    }}
                  />
                }
                label={<Typography variant="body2">Destacar dia atual no calendário</Typography>}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={customColors?.highlightPerfectDay !== false}
                    onChange={() => onUpdateCustomColors({
                      ...customColors,
                      highlightPerfectDay: customColors?.highlightPerfectDay === false
                    })}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: customColors?.primary || 'primary.main'
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: customColors?.primary || 'primary.main'
                      }
                    }}
                  />
                }
                label={<Typography variant="body2">Destacar dia perfeito no calendário</Typography>}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={customColors?.showProgrammedDays !== false}
                    onChange={() => onUpdateCustomColors({
                      ...customColors,
                      showProgrammedDays: customColors?.showProgrammedDays === false
                    })}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: customColors?.primary || 'primary.main'
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: customColors?.primary || 'primary.main'
                      }
                    }}
                  />
                }
                label={<Typography variant="body2">Mencionar dias programados dos hábitos</Typography>}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={showHabitIcons}
                    onChange={() => {
                      setShowHabitIcons(v => !v);
                      onUpdateCustomColors({ ...customColors, showHabitIcons: !showHabitIcons });
                    }}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: customColors?.primary || 'primary.main'
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: customColors?.primary || 'primary.main'
                      }
                    }}
                  />
                }
                label={<Typography variant="body2">Exibir ícones de destaque nos hábitos</Typography>}
              />
            </Box>
            <Divider orientation={isMobile ? 'horizontal' : 'vertical'} flexItem sx={{ my: { xs: 2, sm: 0 } }} />
            {/* Lado Direito: Componentes e Layout */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Componentes e Layout
              </Typography>
              {localComponentsOrder.map((componentId) => {
                const componentNames = {
                  calendar: 'Hábitos',
                  progress: 'Barra de Progresso',
                  donuts: 'Gráficos Donuts',
                  area: 'Gráfico de Área',
                  stats: 'Cards de Estatísticas',
                  events: 'Eventos do Mês'
                };
                return (
                  <Paper key={componentId} sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, p: 1, borderRadius: 1, width: '100%'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Tooltip title={visibleComponents[componentId] ? 'Ocultar' : 'Mostrar'}>
                        <IconButton
                          onClick={() => handleToggleVisibility(componentId)}
                          size="small"
                          sx={{ color: visibleComponents[componentId] ? (customColors?.primary || 'primary.main') : 'text.disabled' }}
                        >
                          {visibleComponents[componentId] ? <VisibilityIcon /> : <VisibilityOffIcon />}
                        </IconButton>
                      </Tooltip>
                      <Typography variant="body2">{componentNames[componentId]}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="Mover para cima">
                        <span>
                          <IconButton
                            onClick={() => handleMoveComponent(componentId, 'up')}
                            disabled={localComponentsOrder.indexOf(componentId) === 0}
                            size="small"
                          >
                            <ArrowUpwardIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Mover para baixo">
                        <span>
                          <IconButton
                            onClick={() => handleMoveComponent(componentId, 'down')}
                            disabled={localComponentsOrder.indexOf(componentId) === localComponentsOrder.length - 1}
                            size="small"
                          >
                            <ArrowDownwardIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </Paper>
                );
              })}
              {hasUnsavedChanges && (
                <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' }, mt: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={handleRestoreDefaultLayout}
                    size="small"
                    sx={{ width: { xs: '100%', sm: 'auto' }, whiteSpace: 'nowrap', borderColor: customColors?.primary || theme.palette.primary.main, color: customColors?.primary || theme.palette.primary.main }}
                  >
                    Layout padrão
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSaveLayout}
                    startIcon={<SaveIcon />}
                    disabled={saveButtonState.disabled}
                    sx={{
                      width: { xs: '100%', sm: 'auto' },
                      whiteSpace: 'nowrap',
                      bgcolor: saveButtonState.color === 'success' ? '#4caf50' : (customColors?.primary || theme.palette.primary.main),
                      color: saveButtonState.color === 'success' ? 'white' : (customColors?.primary ? getContrastText(customColors.primary) : theme.palette.primary.contrastText),
                      transition: 'all 0.3s ease',
                      '&:disabled': { bgcolor: saveButtonState.color === 'success' ? '#4caf50' : undefined, color: saveButtonState.color === 'success' ? 'white' : undefined, opacity: saveButtonState.color === 'success' ? 0.9 : 0.7 }
                    }}
                  >
                    {saveButtonState.text}
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowControls(false)} color="primary" variant="contained">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // Função para verificar se um evento está no dia atual
  const isEventOnDay = (event, day) => {
    if (!event.date || !day) return false;
    
    const eventDate = new Date(event.date);
    const dayDate = new Date(day);
    
    return (
      eventDate.getDate() === dayDate.getDate() &&
      eventDate.getMonth() === dayDate.getMonth() &&
      eventDate.getFullYear() === dayDate.getFullYear()
    );
  };

  // Função para obter os eventos de um dia específico
  const getEventsForDay = (day) => {
    return events.filter(event => isEventOnDay(event, day));
  };

  // Renderiza o conteúdo de um dia no calendário
  const renderDayContent = (day) => {
    const dayEvents = getEventsForDay(day);
    console.log('Renderizando dia:', day.toLocaleDateString(), 'com', dayEvents.length, 'eventos');
    
    return (
      <Box sx={{ position: 'relative', height: '100%' }}>
        <Typography variant="body2" sx={{ textAlign: 'center' }}>
          {day.getDate()}
        </Typography>
        {dayEvents.length > 0 && (
          <Box sx={{ mt: 1 }}>
            {dayEvents.map((event, index) => (
              <Chip
                key={index}
                icon={<EventIcon />}
                label={event.title}
                size="small"
                sx={{
                  width: '100%',
                  mb: 0.5,
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  '& .MuiChip-label': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }
                }}
              />
            ))}
          </Box>
        )}
      </Box>
    );
  };

  const handleEventComplete = async (event) => {
    if (!currentUser?.uid) return;

    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userRef);
      const userData = userDoc.data();
      const events = userData.events || [];
      
      // Encontrar o evento no array
      const eventIndex = events.findIndex(e => e.id === event.id);
      
      if (eventIndex !== -1) {
        // Criar uma cópia do evento com a data original
        const updatedEvent = {
          ...events[eventIndex],
          completed: !events[eventIndex].completed,
          completedAt: !events[eventIndex].completed ? new Date().toISOString() : null,
          date: events[eventIndex].date, // Manter a data original
          time: events[eventIndex].time // Manter o horário original
        };
        
        // Remover o evento antigo e adicionar o atualizado
        await updateDoc(userRef, {
          events: arrayRemove(events[eventIndex])
        });
        
        await updateDoc(userRef, {
          events: arrayUnion(updatedEvent)
        });
        
        // Atualizar o estado local com as datas convertidas
        setEvents(prev => prev.map(e => 
          e.id === event.id ? {
            ...updatedEvent,
            date: new Date(updatedEvent.date),
            time: updatedEvent.time ? new Date(updatedEvent.time) : null
          } : e
        ));
      }
    } catch (error) {
      console.error('Erro ao atualizar evento:', error);
    }
  };

  // Adicionar cálculo de streaks e destaques
  const habitStats = useMemo(() => {
    const stats = {};
    let maxStreak = 0;
    let maxStreakHabitId = null;
    const today = new Date();
    const isCurrentMonth = today.getMonth() === month && today.getFullYear() === year;
    habits.forEach(habit => {
      if (!habit || !habit.id) return;
      // streak global (mantém)
      let streak = 0;
      let maxHabitStreak = 0;
      let lastDate = null;
      const completedDates = (habit.completedDates || []).map(d => d.split('T')[0]).sort();
      // 1. Gerar lista de datas programadas para o hábito no mês, respeitando data de início
      let scheduledDates = [];
      const start = getHabitStartDate(habit);
      for (let day = 1; day <= new Date(year, month + 1, 0).getDate(); day++) {
        const date = new Date(year, month, day);
        const dateStr = date.toISOString().split('T')[0];
        // Se o hábito tem data de início, ignora dias anteriores
        if (start && date < start) {
          continue;
        }
        if (!habit.weekdays?.length || habit.weekdays.includes(convertJSDayToSystemDay(date.getDay()))) {
          scheduledDates.push(dateStr);
        }
      }
      // 2. Calcular maior sequência de datas programadas concluídas (mantém)
      let currentStreak = 0;
      let maxMonthStreak = 0;
      for (let i = 0; i < scheduledDates.length; i++) {
        if (completedDates.includes(scheduledDates[i])) {
          currentStreak++;
          if (currentStreak > maxMonthStreak) maxMonthStreak = currentStreak;
        } else {
          currentStreak = 0;
        }
      }
      if (maxMonthStreak > maxStreak) {
        maxStreak = maxMonthStreak;
        maxStreakHabitId = habit.id;
      }
      // streak atual (mantém)
      for (let i = completedDates.length - 1; i >= 0; i--) {
        const date = completedDates[i];
        if (!lastDate) {
          streak = 1;
          lastDate = date;
        } else {
          const prev = new Date(lastDate);
          prev.setDate(prev.getDate() - 1);
          if (date === prev.toISOString().split('T')[0]) {
            streak++;
            lastDate = date;
          } else {
            break;
          }
        }
      }
      // streak relevante para o foguinho
      let fireStreak = 0;
      if (scheduledDates.length > 0) {
        if (isCurrentMonth) {
          // mês atual: streak terminando hoje
          const todayStr = today.toISOString().split('T')[0];
          let idx = scheduledDates.indexOf(todayStr);
          if (idx !== -1) {
            // Conta streak retroativo a partir de hoje
            for (let i = idx; i >= 0; i--) {
              if (completedDates.includes(scheduledDates[i])) {
                fireStreak++;
              } else {
                break;
              }
            }
          }
        } else {
          // mês passado: streak terminando no último dia programado do mês
          let idx = scheduledDates.length - 1;
          // Pega o último dia programado do mês
          for (; idx >= 0; idx--) {
            if (scheduledDates[idx]) {
              // Conta streak retroativo a partir do último dia programado
              for (let j = idx; j >= 0; j--) {
                if (completedDates.includes(scheduledDates[j])) {
                  fireStreak++;
                } else {
                  break;
                }
              }
              break;
            }
          }
        }
      }
      let scheduled = scheduledDates.length;
      let completed = scheduledDates.filter(dateStr => completedDates.includes(dateStr)).length;
      stats[habit.id] = {
        streak, // streak global
        maxMonthStreak,
        scheduled,
        completed,
        allCompleted: scheduled > 0 && completed === scheduled,
        lowCompletion: scheduled > 0 && completed / scheduled < 0.5,
        fireStreak // streak relevante para o foguinho
      };
    });
    return { stats, maxStreakHabitId };
  }, [habits, month, year]);

  if (habits.length === 0) {
    return (
      <Box sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
          Você ainda não cadastrou nenhum hábito!
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Para começar a usar o calendário e marcar seus hábitos diários, cadastre seu primeiro hábito na aba "Hábitos". Assim que você adicionar um hábito, ele aparecerá aqui para acompanhamento diário.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Ano, Mês e Configurações em linha no mobile */}
      {isMobile && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1, gap: 1 }}>
          <TextField
            type="number"
            label="Ano"
            value={year}
            onChange={e => {
              const newYear = parseInt(e.target.value, 10);
              if (!isNaN(newYear) && onYearChange) {
                onYearChange(newYear);
              }
            }}
            size="small"
            sx={{ width: 90 }}
            inputProps={{ min: 2000, max: 2100, step: 1 }}
          />
          <FormControl size="small" sx={{ width: 120 }}>
            <InputLabel id="select-mes-label">Mês</InputLabel>
            <Select
              labelId="select-mes-label"
              value={month}
              label="Mês"
              onChange={e => {
                if (typeof onMonthChange === 'function') {
                  onMonthChange(Number(e.target.value));
                }
              }}
            >
              {months.map((monthName, index) => (
                <MenuItem key={monthName} value={index}>{monthName}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <IconButton
            onClick={() => setShowControls(!showControls)}
            sx={{ 
              color: showControls ? (customColors?.primary || 'primary.main') : 'text.secondary',
              ml: 1
            }}
          >
            <SettingsIcon />
          </IconButton>
        </Box>
      )}
      {/* Meses, Ano (desktop) e Controles de Visualização */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: { xs: 1, sm: 2 },
        px: { xs: 1, sm: 0 }
      }}>
        {!isMobile && (
          <>
            <Box 
              ref={monthsContainerRef}
              sx={{ 
                display: 'flex', 
                gap: 1,
                overflowX: 'auto',
                flex: { xs: 1, md: '0 1 auto' },
                maxWidth: { xs: '100%', md: 'fit-content' },
                mx: { xs: 0, md: 'auto' },
                justifyContent: 'flex-start',
                '&::-webkit-scrollbar': { display: 'none' },
                msOverflowStyle: 'none',
              }}
            >
              {months.map((monthName, index) => (
                <Button
                  key={index}
                  variant={month === index ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => {
                    if (typeof onMonthChange === 'function') {
                      onMonthChange(index);
                    }
                  }}
                  sx={{
                    minWidth: 'auto',
                    px: 2,
                    whiteSpace: 'nowrap',
                    bgcolor: month === index ? (customColors?.primary || 'primary.main') : 'transparent',
                    color: month === index ? 
                      (customColors?.primary ? getContrastText(customColors.primary) : theme.palette.primary.contrastText) : 
                      (customColors?.primary || theme.palette.primary.main),
                    borderColor: customColors?.primary || theme.palette.primary.main,
                    '&:hover': {
                      bgcolor: month === index ? 
                        (customColors?.primary || 'primary.main') : 
                        alpha(customColors?.primary || theme.palette.primary.main, 0.04),
                      opacity: month === index ? 0.9 : 1
                    }
                  }}
                >
                  {monthName}
                </Button>
              ))}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                type="number"
                label="Ano"
                value={year}
                onChange={e => {
                  const newYear = parseInt(e.target.value, 10);
                  if (!isNaN(newYear) && onYearChange) {
                    onYearChange(newYear);
                  }
                }}
                size="small"
                sx={{ 
                  width: 90, 
                  ml: 2,
                  '& .MuiInputBase-input': {
                    color: customColors?.primary || 'primary.main',
                    fontWeight: 500
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: customColors?.primary || 'primary.main'
                  },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: customColors?.primary || 'primary.main'
                  }
                }}
                inputProps={{ min: 2000, max: 2100, step: 1 }}
              />
              <IconButton
                onClick={() => setShowControls(!showControls)}
                sx={{ 
                  color: showControls ? (customColors?.primary || 'primary.main') : 'text.secondary',
                  ml: 2
                }}
              >
                <SettingsIcon />
              </IconButton>
              <IconButton
                onClick={() => setHelpOpen(true)}
                sx={{ color: 'text.secondary' }}
              >
                <HelpOutlineIcon />
              </IconButton>
            </Box>
          </>
        )}
      </Box>

      {/* Citação do Dia */}
      {customColors?.showDailyQuote !== false && (
        <Paper
          elevation={0}
          sx={{
            p: { xs: 1, sm: 1.5 },
            mb: { xs: 1, sm: 2 },
            backgroundColor: 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            flexWrap: 'wrap', 
            textAlign: 'center'
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              color: customColors?.primary || theme.palette.primary.main,
              fontWeight: 'bold'
            }}
          >
            {format(new Date(), "dd 'de' MMMM',' yyyy", { locale: ptBR })}:
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              color: theme.palette.text.primary,
              fontStyle: 'italic'
            }}
          >
            "{getQuoteForDay().text}" - {getQuoteForDay().author}
          </Typography>
        </Paper>
      )}

      {/* Controles e Filtros */}
      {renderControls()}

      {/* Renderiza todos os componentes na ordem definida */}
      {localComponentsOrder.map(componentId => renderComponent(componentId))}

      {/* Modal de ajuda da aba calendário */}
      <Dialog open={helpOpen} onClose={() => setHelpOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold', color: customColors?.primary || theme.palette.primary.main }}>
          Como funciona a aba Calendário?
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Indicadores e Ícones</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WhatshotIcon fontSize="medium" color="warning" />
                <Typography variant="body1"><b>Streak (Foguinho):</b> Hábito mantido em sequência. No mês atual, o foguinho aparece se você está mantendo o hábito até hoje. Em meses passados, aparece se terminou o mês com uma sequência. Precisa de pelo menos 3 dias programados seguidos.</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmojiEventsIcon fontSize="medium" color="success" />
                <Typography variant="body1"><b>Troféu:</b> Hábito com a maior sequência de dias programados concluídos no mês.</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <StarIcon fontSize="medium" color="primary" />
                <Typography variant="body1"><b>Estrela:</b> Hábito realizado em todos os dias programados do mês.</Typography>
              </Box>
            </Box>
            <Typography variant="h6" sx={{ mb: 1 }}>Conceitos Importantes</Typography>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body1" sx={{ mb: 1 }}><b>Dias programados:</b> São os dias em que você definiu que o hábito deve ser feito (ex: só segundas e quartas, ou todos os dias). Só esses dias contam para os gráficos e indicadores.</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}><b>Dias não programados:</b> São ignorados nos gráficos e não afetam negativamente seus resultados.</Typography>
            </Box>
            <Typography variant="h6" sx={{ mb: 1 }}>Gráficos Donuts e Progresso</Typography>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                Os gráficos mostram seu desempenho apenas nos dias programados para cada hábito. Dias não programados não diminuem sua performance!
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <b>Gráficos Donuts:</b> Mostram o percentual de hábitos concluídos por semana.
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <b>Barra de Progresso:</b> Mostra o percentual de hábitos programados concluídos no mês.
              </Typography>
            </Box>
            <Typography variant="h6" sx={{ mb: 1 }}>Como usar a aba Calendário?</Typography>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                - <b>Lance seus hábitos</b> na aba <b>Hábitos</b>. Eles aparecerão automaticamente no calendário nos dias programados.
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                - <b>Lance seus eventos</b> na aba <b>Eventos</b> para visualizar compromissos junto com seus hábitos.
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                - <b>O calendário</b> ajuda a visualizar, marcar e acompanhar seus hábitos, identificar padrões, manter a motivação e celebrar conquistas!
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                - <b>Você pode mudar o layout</b> dos componentes do calendário para deixar a visualização do seu jeito.
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                - <b>Personalize sons e cores</b> na aba <b>Configurações</b> para deixar a experiência com a sua cara!
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2 }}>
              Dica: Clique nos quadradinhos para marcar/desmarcar hábitos realizados. Use os ícones para entender seu progresso!
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelpOpen(false)} color="primary" variant="contained">Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de edição rápida de hábito (nome, dias e pilar) */}
      <Dialog open={!!editingHabit} onClose={handleCloseEditHabit} maxWidth="xs" fullWidth>
        <DialogTitle>Editar Hábito</DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            margin="dense"
            label="Nome do hábito"
            type="text"
            fullWidth
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
          />
          <FormGroup sx={{ mt: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={editActive}
                  onChange={(e) => setEditActive(e.target.checked)}
                  size="small"
                />
              }
              label="Hábito ativo"
            />
          </FormGroup>
          <TextField
            margin="dense"
            label="Começou em"
            type="date"
            fullWidth
            value={editStartDate}
            onChange={(e) => setEditStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mt: 2 }}
          />
          <TextField
            select
            margin="dense"
            fullWidth
            label="Pilar"
            value={editPillar}
            onChange={(e) => setEditPillar(e.target.value)}
            SelectProps={{ native: true }}
            sx={{ mt: 2 }}
          >
            <option value="vitalidade">Vitalidade (Combustível)</option>
            <option value="mentalidade">Mentalidade (Controle)</option>
            <option value="prosperidade">Prosperidade (Alicerce)</option>
          </TextField>
          <FormGroup sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Dias da semana
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {WEEKDAYS.map((day) => (
                <FormControlLabel
                  key={day.id}
                  control={
                    <Switch
                      checked={editWeekdays.includes(day.id)}
                      onChange={() => handleEditDayToggle(day.id)}
                      size="small"
                    />
                  }
                  label={day.name}
                />
              ))}
            </Box>
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditHabit}>Cancelar</Button>
          <Button onClick={handleSaveEditHabit} variant="contained">
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default MonthCalendar; 